// src/pages/KCGSandbox2.jsx
import React, { useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Accordion from "react-bootstrap/Accordion";

import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import ActionButton from "../utils/ActionButton";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

import { Users, Building2, MapPinned, ToggleLeft, ToggleRight } from "lucide-react";

import "../styles/AboutUs.css";
import "../styles/Aerospace.css";
import "../styles/UtahVerticals.css";

// -------- BRAND (Gardner-ish + UtahData polish) --------
const BRAND = {
  red: "#CC0000",
  ink: "#0B1220", // text is fine; charts won't use black
  slate: "#4B5563",
  mist: "#F5F7FB",
  card: "#FFFFFF",
  ring: "rgba(204,0,0,0.18)",
};

// -------- CHART THEME (no black strokes) --------
const CHART = {
  axis: "#94A3B8",
  grid: "rgba(148,163,184,0.28)",
  tooltipBorder: "rgba(15,23,42,0.10)",
  tooltipBg: "rgba(255,255,255,0.97)",
  // series colors
  a: "#CC0000", // red
  b: "#2563EB", // blue
  c: "#7C3AED", // purple
  d: "#0EA5E9", // cyan
  e: "#F59E0B", // amber
};

const clamp = (n, a = 0, b = 100) => Math.max(a, Math.min(b, n));
const pct = (n, d = 1) => `${Number(n || 0).toFixed(d)}%`;
const fmt = {
  int: (n) => Number(n || 0).toLocaleString(),
  usd0: (n) =>
    Number(n || 0).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }),
  usd1: (n) =>
    Number(n || 0).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 1,
    }),
  num1: (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }),
};

// ----------------------
// DATA (from attached ERG2026.pdf only)
// ----------------------

// Table 27.1 (Utah Projected Population by County, 2025–2065)
const POP_BY_COUNTY = [
  { county: "Beaver", y2025: 7533, y2035: 7784, y2045: 7992, y2055: 8217, y2065: 8389 },
  { county: "Box Elder", y2025: 62339, y2035: 68543, y2045: 76852, y2055: 86166, y2065: 94736 },
  { county: "Cache", y2025: 145171, y2035: 161047, y2045: 181374, y2055: 203761, y2065: 224129 },
  { county: "Carbon", y2025: 20428, y2035: 20887, y2045: 22593, y2055: 24713, y2065: 25926 },
  { county: "Daggett", y2025: 984, y2035: 995, y2045: 1020, y2055: 1057, y2065: 1075 },
  { county: "Davis", y2025: 381508, y2035: 413783, y2045: 464130, y2055: 519234, y2065: 569141 },
  { county: "Duchesne", y2025: 20186, y2035: 21568, y2045: 22812, y2055: 24659, y2065: 25571 },
  { county: "Emery", y2025: 10193, y2035: 10245, y2045: 11054, y2055: 12059, y2065: 12635 },
  { county: "Garfield", y2025: 5119, y2035: 5236, y2045: 5373, y2055: 5520, y2065: 5633 },
  { county: "Grand", y2025: 9903, y2035: 10480, y2045: 11326, y2055: 12417, y2065: 13376 },
  { county: "Iron", y2025: 69982, y2035: 87577, y2045: 99265, y2055: 112444, y2065: 123138 },
  { county: "Juab", y2025: 13386, y2035: 15635, y2045: 17439, y2055: 19436, y2065: 21259 },
  { county: "Kane", y2025: 8398, y2035: 8774, y2045: 9088, y2055: 9427, y2065: 9688 },
  { county: "Millard", y2025: 13704, y2035: 14707, y2045: 15629, y2055: 16417, y2065: 16940 },
  { county: "Morgan", y2025: 13117, y2035: 14166, y2045: 17069, y2055: 20441, y2065: 23643 },
  { county: "Piute", y2025: 1701, y2035: 2129, y2045: 2248, y2055: 2344, y2065: 2402 },
  { county: "Rich", y2025: 2859, y2035: 3302, y2045: 3542, y2055: 3807, y2065: 4049 },
  { county: "Salt Lake", y2025: 1241601, y2035: 1322469, y2045: 1420497, y2055: 1522607, y2065: 1611928 },
  { county: "San Juan", y2025: 15037, y2035: 16039, y2045: 16885, y2055: 17977, y2065: 18936 },
  { county: "Sanpete", y2025: 31444, y2035: 36605, y2045: 41101, y2055: 45037, y2065: 47738 },
  { county: "Sevier", y2025: 21943, y2035: 22458, y2045: 23905, y2055: 25002, y2065: 25643 },
  { county: "Summit", y2025: 43374, y2035: 45164, y2045: 49528, y2055: 53571, y2065: 56650 },
  { county: "Tooele", y2025: 83733, y2035: 100653, y2045: 117621, y2055: 136405, y2065: 153551 },
  { county: "Uintah", y2025: 36428, y2035: 40683, y2045: 44360, y2055: 49820, y2065: 52515 },
  { county: "Utah", y2025: 772019, y2035: 966949, y2045: 1149646, y2055: 1354791, y2065: 1543744 },
  { county: "Wasatch", y2025: 39428, y2035: 46568, y2045: 58072, y2055: 71437, y2065: 84126 },
  { county: "Washington", y2025: 209084, y2035: 250602, y2045: 293296, y2055: 343511, y2065: 384339 },
  { county: "Wayne", y2025: 2555, y2035: 2858, y2045: 3266, y2055: 3621, y2065: 3861 },
  { county: "Weber", y2025: 273234, y2035: 292813, y2045: 322935, y2055: 355904, y2065: 385763 },
  { county: "State of Utah", y2025: 3556394, y2035: 4010720, y2045: 4509916, y2055: 5061804, y2065: 5550525 },
];

// Table 27.3 (Utah Demographic Projections by Selected Age Groups, 2025–2065)
const AGE_GROUPS = [
  { year: 2025, total: 3556394, medianAge: 32.82, school: 682644, schoolShare: 19.2, working: 2191080, workingShare: 61.6, retire: 452840, retireShare: 12.7 },
  { year: 2035, total: 4010720, medianAge: 35.7, school: 596748, schoolShare: 14.9, working: 2550969, workingShare: 63.6, retire: 610041, retireShare: 15.2 },
  { year: 2045, total: 4509916, medianAge: 38.97, school: 663906, schoolShare: 14.7, working: 2764741, workingShare: 61.3, retire: 816479, retireShare: 18.1 },
  { year: 2055, total: 5061804, medianAge: 42.69, school: 710601, schoolShare: 14.0, working: 3044771, workingShare: 60.2, retire: 1051913, retireShare: 20.8 },
  { year: 2065, total: 5550525, medianAge: 45.33, school: 688198, schoolShare: 12.4, working: 3285336, workingShare: 59.2, retire: 1307065, retireShare: 23.5 },
];

// Consumer Sentiment chapter (Utah points + annual averages)
const SENTIMENT_POINTS = [
  { label: "Jan 2025", utah: 91.3, usAvg: 57.6 },
  { label: "Apr 2025", utah: 79.9, usAvg: 57.6 },
  { label: "Oct 2025", utah: 73.4, usAvg: 57.6 },
  { label: "Dec 2025", utah: 79.9, usAvg: 57.6 },
];

const SENTIMENT_AVG = [
  { year: "2022", utahAvg: 68.4 },
  { year: "2023", utahAvg: 73.5 },
  { year: "2024", utahAvg: 81.1 },
  { year: "2025", utahAvg: 81.0, usAvg: 57.6 },
];

// Financial Services chapter (Fintech/Financial Services KPIs)
const FIN_SERV = {
  gdpSharePct: 9.1,
  depositsUtOfficesB: 1000,
  utBanksAssetsB: 1100,
  creditUnionAssetsB: 65,
};

// Life Sciences chapter KPIs (2024)
const LIFE_SCI = {
  establishments: 1581,
  counties: 21,
  jobs: 39880,
  wagesB: 3.7,
  avgGrowthPct: 2.8,
  otherIndustriesGrowthPct: 2.4,
  avgWage: 92900,
  otherAvgWage: 65100,
  wagePremiumPct: 42.7,
};

// Table 24.1 (Residential and Nonresidential Construction Activity, 1970–2026f)
const CONSTRUCTION = [
  { year: "2020", single: 15920, multi: 16014, mobile: 316, totalUnits: 32250, totalValueM: 11250.1 },
  { year: "2021", single: 17635, multi: 22264, mobile: 245, totalUnits: 40144, totalValueM: 13715.6 },
  { year: "2022", single: 11944, multi: 17735, mobile: 204, totalUnits: 29833, totalValueM: 12730.2 },
  { year: "2023", single: 10200, multi: 15009, mobile: 236, totalUnits: 25445, totalValueM: 12389.9 },
  { year: "2024", single: 10588, multi: 11143, mobile: 235, totalUnits: 21966, totalValueM: 12281.1 },
  { year: "2025e", single: 10200, multi: 13500, mobile: 150, totalUnits: 23850, totalValueM: 13525.0 },
  { year: "2026f", single: 11000, multi: 12000, mobile: 200, totalUnits: 23200, totalValueM: 11750.0 },
];

// Housing affordability / rents (Real Estate chapter)
const HOUSING = {
  medianHomePrice: 510000,
  incomeNeeded: 149000,
  incomeIncreaseYoYPct: 1.5,
  incomeIncreaseSince2020Pct: 90.7,
  homeownershipUtahPct: 68.3,
  rentAskingQ3_2025: 1349,
  rentYoYPct: -1.8,
  priceOutlookPctLow: 1.0,
  priceOutlookPctHigh: 3.0,
};
// =======================
// SECTION B ONLY — COMPONENT + DATA + HELPERS (SCOPED)
// =======================
function SectionBImpact({ BRAND }) {
  const LIFE_SCI_2024 = [
    { segment: "Medical Devices & Equipment", employment: 12977, sharePct: 32.5, avgWage: 82800 },
    { segment: "Research, Testing & Medical Labs", employment: 11890, sharePct: 29.8, avgWage: 97800 },
    { segment: "Drugs & Pharmaceuticals", employment: 7715, sharePct: 19.4, avgWage: 102150 },
    { segment: "Bio-Science Related Distribution", employment: 7298, sharePct: 18.3, avgWage: 124900 },
  ];

  const TECH_FINTECH = {
    employment: { core: 65813, supporting: 12132, total: 77946 },
    avgWage: { core: 9572, supporting: 768, total: 10340 },
    wageSharePct: { core: 93, supporting: 7 },
  };

  const FIN_SERV_2025 = [
    { type: "Banks and Thrifts", count: 62, assetsLabel: "$1.1 Trillion" },
    { type: "Industrial Banks (ILCs)", count: 16, assetsLabel: "$223 Billion" },
    { type: "Credit Unions", count: 54, assetsLabel: "$65 Billion" },
  ];

  const CHART = {
    purple: "#7C3AED",
    green: "#10B981",
    blue: "#2563EB",
    orange: "#F97316",
    grid: "rgba(15,23,42,0.10)",
    axis: "#64748B",
    tick: "#475569",
  };

  const axisProps = {
    axisLine: { stroke: CHART.axis },
    tickLine: { stroke: "rgba(100,116,139,0.35)" },
    tick: { fill: CHART.tick, fontWeight: 800, fontSize: 12 },
  };

  const fmt = {
    int: (n) => Number(n).toLocaleString(),
    usd0: (n) =>
      Number(n).toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }),
    pct1: (n) => `${n.toFixed(1)}%`,
  };

  const lifeTotal = LIFE_SCI_2024.reduce((s, x) => s + x.employment, 0);
  const weightedWage =
    LIFE_SCI_2024.reduce((s, x) => s + x.employment * x.avgWage, 0) / lifeTotal;

  const assetsToB = (l) =>
    l.includes("Trillion") ? parseFloat(l) * 1000 : parseFloat(l);

  const finAssets = FIN_SERV_2025.map((x) => ({
    ...x,
    assetsB: assetsToB(x.assetsLabel.replace("$", "")),
  }));

  const biggest = [...finAssets].sort((a, b) => b.assetsB - a.assetsB)[0];

  const lifeEmploymentBars = LIFE_SCI_2024.map((x) => ({ name: x.segment, value: x.employment }));
  const lifeWageBars = LIFE_SCI_2024.map((x) => ({ name: x.segment, value: x.avgWage }));
  const techBars = [
    { name: "Core", value: TECH_FINTECH.employment.core },
    { name: "Supporting", value: TECH_FINTECH.employment.supporting },
    { name: "Total", value: TECH_FINTECH.employment.total },
  ];
  const finBars = finAssets.map((x) => ({ name: x.type, value: x.assetsB }));

  return (
    <div>
      {/* KPI Row */}
      <Row className="g-3 mb-3">
        <Col md={3}><MiniKpi label="Life Sciences Jobs" value={fmt.int(lifeTotal)} tone="red" /></Col>
        <Col md={3}><MiniKpi label="Weighted Avg Wage" value={fmt.usd0(weightedWage)} /></Col>
        <Col md={3}><MiniKpi label="Tech Employment" value={fmt.int(TECH_FINTECH.employment.total)} /></Col>
        <Col md={3}><MiniKpi label="Largest Assets" value={biggest.assetsLabel} tone="red" /></Col>
      </Row>

      {/* Life Sciences Charts */}
      <Row className="g-3">
        <Col lg={7}>
          <CardShell>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>Life Sciences Employment</div>
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={lifeEmploymentBars} layout="vertical" margin={{ left: 28 }}>
                  <CartesianGrid stroke={CHART.grid} />
                  <XAxis type="number" tickFormatter={(v) => `${v / 1000}k`} {...axisProps} />
                  <YAxis type="category" dataKey="name" width={220} {...axisProps} />
                  <Tooltip formatter={(v) => fmt.int(v)} />
                  <Bar dataKey="value" fill={CHART.purple} radius={[0, 10, 10, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardShell>
        </Col>

        <Col lg={5}>
          <CardShell>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>Life Sciences Avg Wages</div>
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={lifeWageBars} layout="vertical" margin={{ left: 28 }}>
                  <CartesianGrid stroke={CHART.grid} />
                  <XAxis type="number" tickFormatter={(v) => `$${v / 1000}k`} {...axisProps} />
                  <YAxis type="category" dataKey="name" width={220} {...axisProps} />
                  <Tooltip formatter={(v) => fmt.usd0(v)} />
                  <Bar dataKey="value" fill={CHART.green} radius={[0, 10, 10, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardShell>
        </Col>
      </Row>

      {/* Tech + Financial Services */}
      <Row className="g-3 mt-3">
        <Col lg={6}>
          <CardShell>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>Tech / Fintech Employment</div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={techBars}>
                  <CartesianGrid stroke={CHART.grid} />
                  <XAxis dataKey="name" {...axisProps} />
                  <YAxis tickFormatter={(v) => `${v / 1000}k`} {...axisProps} />
                  <Tooltip formatter={(v) => fmt.int(v)} />
                  <Bar dataKey="value" fill={CHART.blue} radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardShell>
        </Col>

        <Col lg={6}>
          <CardShell>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>Financial Services Assets (Billions)</div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={finBars}>
                  <CartesianGrid stroke={CHART.grid} />
                  <XAxis dataKey="name" {...axisProps} />
                  <YAxis tickFormatter={(v) => `${v}B`} {...axisProps} />
                  <Tooltip formatter={(v) => `${v}B`} />
                  <Bar dataKey="value" fill={CHART.orange} radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardShell>
        </Col>
      </Row>
    </div>
  );
}


// ----------------------
// Small UI helpers
// ----------------------
function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-0">
      <div className="d-flex align-items-center gap-2">
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 14,
            background: `linear-gradient(135deg, ${BRAND.ring}, rgba(17,24,39,0.04))`,
            display: "grid",
            placeItems: "center",
            border: "1px solid rgba(204,0,0,0.18)",
          }}
        >
          <Icon size={22} color={BRAND.red} />
        </div>
        <div>
          <div style={{ fontWeight: 1000, color: BRAND.ink, letterSpacing: -0.3, fontSize: 18 }}>
            {title}
          </div>
          {subtitle ? (
            <div style={{ color: BRAND.slate, fontSize: 14.5, marginTop: 3, lineHeight: 1.35 }}>
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CardShell({ children, style }) {
  return (
    <div
      style={{
        background: BRAND.card,
        borderRadius: 18,
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
        padding: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function MiniKpi({ label, value, hint, tone = "neutral" }) {
  const border =
    tone === "red"
      ? `1px solid rgba(204,0,0,0.25)`
      : tone === "dark"
        ? `1px solid rgba(15,23,42,0.18)`
        : "1px solid rgba(15,23,42,0.10)";

  const top =
    tone === "red"
      ? `linear-gradient(135deg, rgba(204,0,0,0.10), rgba(255,255,255,0.9))`
      : `linear-gradient(135deg, rgba(15,23,42,0.05), rgba(255,255,255,0.9))`;

  return (
    <div style={{ border, borderRadius: 16, padding: 12, background: top, height: "100%" }}>
      <div style={{ fontSize: 11, fontWeight: 900, color: BRAND.slate, textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 1000, marginTop: 5, color: BRAND.ink, letterSpacing: -0.3 }}>
        {value}
      </div>
      {hint ? (
        <div style={{ marginTop: 6, fontSize: 12.5, color: BRAND.slate, lineHeight: 1.35 }}>{hint}</div>
      ) : null}
    </div>
  );
}

function NiceSlider({ value, min, max, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <div
        style={{
          fontWeight: 1000,
          color: BRAND.ink,
          padding: "8px 12px",
          borderRadius: 14,
          border: "1px solid rgba(15,23,42,0.10)",
          background: "rgba(255,255,255,0.85)",
        }}
      >
        Year: <span style={{ color: BRAND.red }}>{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: 340, maxWidth: "100%" }}
      />
      <div style={{ fontSize: 12.5, color: BRAND.slate }}>Drag to explore projections</div>
    </div>
  );
}

// ----------------------
// Chart helpers (no-black)
// ----------------------
function SoftTooltip({ active, label, payload, formatterOverride }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: CHART.tooltipBg,
        border: `1px solid ${CHART.tooltipBorder}`,
        borderRadius: 14,
        padding: 10,
        boxShadow: "0 18px 50px rgba(15,23,42,0.12)",
        color: BRAND.ink,
        minWidth: 190,
      }}
    >
      <div style={{ fontWeight: 1000, marginBottom: 6 }}>{label}</div>
      <div style={{ display: "grid", gap: 4 }}>
        {payload.map((p, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <span style={{ color: BRAND.slate, fontWeight: 900 }}>{p.name}</span>
            <span style={{ fontWeight: 1000 }}>
              {formatterOverride ? formatterOverride(p.value, p) : p.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const axisProps = {
  stroke: CHART.axis,
  tick: { fill: CHART.axis, fontWeight: 800 },
  axisLine: { stroke: CHART.axis },
  tickLine: { stroke: CHART.axis },
};

const gridProps = { stroke: CHART.grid, strokeDasharray: "3 3" };

// ----------------------
// Math helpers
// ----------------------
function interpolate(year, series) {
  const y = Number(year);
  const s = [...series].sort((a, b) => a.year - b.year);

  if (y <= s[0].year) return s[0].value;
  if (y >= s[s.length - 1].year) return s[s.length - 1].value;

  for (let i = 0; i < s.length - 1; i++) {
    const a = s[i];
    const b = s[i + 1];
    if (y >= a.year && y <= b.year) {
      const t = (y - a.year) / (b.year - a.year);
      return a.value + (b.value - a.value) * t;
    }
  }
  return s[0].value;
}

function countyPopAtYear(row, year) {
  const series = [
    { year: 2025, value: row.y2025 },
    { year: 2035, value: row.y2035 },
    { year: 2045, value: row.y2045 },
    { year: 2055, value: row.y2055 },
    { year: 2065, value: row.y2065 },
  ];
  return Math.round(interpolate(year, series));
}

function demoAtYear(year) {
  const y = Number(year);

  const nearest = (field) =>
    Math.round(interpolate(y, AGE_GROUPS.map((r) => ({ year: r.year, value: r[field] }))));

  const nearest1 = (field) =>
    Number(interpolate(y, AGE_GROUPS.map((r) => ({ year: r.year, value: r[field] }))).toFixed(2));

  return {
    total: nearest("total"),
    medianAge: nearest1("medianAge"),
    school: nearest("school"),
    schoolShare: nearest1("schoolShare"),
    working: nearest("working"),
    workingShare: nearest1("workingShare"),
    retire: nearest("retire"),
    retireShare: nearest1("retireShare"),
  };
}
// =======================
// SECTION D ONLY — COMPONENT + DATA + HELPERS (SCOPED)
// =======================
function SectionDConstruction({ BRAND }) {
  // ---------- Section D DATA (ONLY from your provided tables) ----------
  const PERMIT_OVERVIEW = {
    residentialValueM: { y2024: 6211, y2025: 6825, y2026: 7300 },
    nonresValueM: { y2024: 2567, y2025: 3750, y2026: 2500 },
  };

  const RES_TRENDS = {
    totalPermits: { y2024: 21966, y2025: 23850, y2026: 23200 },
    singleFamily: { y2024: 10588, y2025: 10200, y2026: 11000 },
    multiFamily: { y2024: 11143, y2025: 13500, y2026: 12000 },
    mobile: { y2024: 235, y2025: 150, y2026: 200 },
    homeownershipPct: { y2024: 69.8, y2025: 68.5, y2026: 68.1 },
  };

  const NONRES_VALUE_M = [
    { metric: "Office/Bank/Professional", y2024: 304, y2025: 300, y2026: 250 },
    { metric: "Retail/Mercantile/Restaurant", y2024: 155, y2025: 450, y2026: 240 },
    { metric: "Industrial/Warehouse/Manufacturing", y2024: 643, y2025: 2300, y2026: 700 },
    { metric: "Structures Other Than Buildings", y2024: 472, y2025: 510, y2026: 425 },
    { metric: "Remaining Nonres. Buildings", y2024: 1046, y2025: 940, y2026: 1335 },
  ];

  // ---------- Section D CHART PALETTE (no black) ----------
  const CHART = {
    blue: "#2563EB",
    purple: "#7C3AED",
    orange: "#F97316",
    green: "#10B981",
    rose: "#F43F5E",
    grid: "rgba(15,23,42,0.10)",
    axis: "#64748B",
    tick: "#475569",
  };

  const axisProps = {
    axisLine: { stroke: CHART.axis },
    tickLine: { stroke: "rgba(100,116,139,0.35)" },
    tick: { fill: CHART.tick, fontWeight: 800, fontSize: 12 },
  };
  const gridProps = { stroke: CHART.grid, strokeDasharray: "3 3" };

  // ---------- helpers (scoped only to Section D) ----------
  const fmt = {
    int: (n) => Number(n || 0).toLocaleString(),
    pct1: (n) => `${Number(n || 0).toFixed(1)}%`,
    usdM0: (n) =>
      `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}M`,
  };

  const safeDiv = (a, b) => (b ? a / b : 0);
  const yoyPct = (a, b) => safeDiv(a - b, b) * 100; // a=current, b=prev
  const delta = (a, b) => a - b;

  function SoftTooltip({ active, payload, label, formatValue }) {
    if (!active || !payload?.length) return null;
    return (
      <div
        style={{
          background: "rgba(255,255,255,0.96)",
          border: "1px solid rgba(15,23,42,0.10)",
          borderRadius: 14,
          boxShadow: "0 18px 55px rgba(2,6,23,0.12)",
          padding: "10px 12px",
          color: BRAND.ink,
          minWidth: 180,
        }}
      >
        <div style={{ fontWeight: 1000, marginBottom: 6, color: BRAND.ink }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 4 }}>
            <div style={{ color: BRAND.slate, fontWeight: 900 }}>{p.name}</div>
            <div style={{ fontWeight: 1000, color: BRAND.ink }}>
              {formatValue ? formatValue(p.value, p.name) : p.value}
            </div>
          </div>
        ))}
      </div>
    );
  }

  function CardShell({ children, style }) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 18,
          border: "1px solid rgba(15,23,42,0.08)",
          boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
          padding: 16,
          ...style,
        }}
      >
        {children}
      </div>
    );
  }

  function MiniKpi({ label, value, hint, tone = "neutral", icon: Icon }) {
    const border =
      tone === "red"
        ? `1px solid rgba(204,0,0,0.25)`
        : tone === "dark"
          ? `1px solid rgba(15,23,42,0.18)`
          : "1px solid rgba(15,23,42,0.10)";

    const top =
      tone === "red"
        ? `linear-gradient(135deg, rgba(204,0,0,0.10), rgba(255,255,255,0.96))`
        : `linear-gradient(135deg, rgba(15,23,42,0.05), rgba(255,255,255,0.96))`;

    return (
      <div style={{ border, borderRadius: 16, padding: 12, background: top, height: "100%" }}>
        <div className="d-flex align-items-center justify-content-between">
          <div style={{ fontSize: 11, fontWeight: 900, color: BRAND.slate, textTransform: "uppercase" }}>{label}</div>
          {Icon ? (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 12,
                display: "grid",
                placeItems: "center",
                background: "rgba(204,0,0,0.08)",
                border: "1px solid rgba(204,0,0,0.18)",
              }}
            >
              <Icon size={16} color={BRAND.red} />
            </div>
          ) : null}
        </div>
        <div style={{ fontSize: 22, fontWeight: 1000, marginTop: 6, color: BRAND.ink, letterSpacing: -0.3 }}>
          {value}
        </div>
        {hint ? <div style={{ marginTop: 6, fontSize: 12.5, color: BRAND.slate, lineHeight: 1.35 }}>{hint}</div> : null}
      </div>
    );
  }

  // ---------- Derived KPIs (ONLY from Section D numbers) ----------
  // Permit overview totals
  const totalPermitValueM_2024 = PERMIT_OVERVIEW.residentialValueM.y2024 + PERMIT_OVERVIEW.nonresValueM.y2024;
  const totalPermitValueM_2025 = PERMIT_OVERVIEW.residentialValueM.y2025 + PERMIT_OVERVIEW.nonresValueM.y2025;
  const totalPermitValueM_2026 = PERMIT_OVERVIEW.residentialValueM.y2026 + PERMIT_OVERVIEW.nonresValueM.y2026;

  // Shares
  const resShare2025 = safeDiv(PERMIT_OVERVIEW.residentialValueM.y2025, totalPermitValueM_2025) * 100;
  const nonresShare2025 = 100 - resShare2025;

  // Residential permit mix shares (by units)
  const mixShare = (part, total) => safeDiv(part, total) * 100;
  const mix2025 = {
    single: mixShare(RES_TRENDS.singleFamily.y2025, RES_TRENDS.totalPermits.y2025),
    multi: mixShare(RES_TRENDS.multiFamily.y2025, RES_TRENDS.totalPermits.y2025),
    mobile: mixShare(RES_TRENDS.mobile.y2025, RES_TRENDS.totalPermits.y2025),
  };

  // Nonres composition for 2025
  const nonres2025Sum = NONRES_VALUE_M.reduce((s, r) => s + r.y2025, 0);
  const nonres2025 = NONRES_VALUE_M.map((r) => ({
    name: r.metric,
    value: r.y2025,
    share: mixShare(r.y2025, nonres2025Sum),
  })).sort((a, b) => b.value - a.value);

  const topNonres2025 = nonres2025[0];

  // ---------- Charts ----------
  const permitValueTrend = [
    {
      year: "2024",
      Residential: PERMIT_OVERVIEW.residentialValueM.y2024,
      Nonresidential: PERMIT_OVERVIEW.nonresValueM.y2024,
      Total: totalPermitValueM_2024,
    },
    {
      year: "2025",
      Residential: PERMIT_OVERVIEW.residentialValueM.y2025,
      Nonresidential: PERMIT_OVERVIEW.nonresValueM.y2025,
      Total: totalPermitValueM_2025,
    },
    {
      year: "2026",
      Residential: PERMIT_OVERVIEW.residentialValueM.y2026,
      Nonresidential: PERMIT_OVERVIEW.nonresValueM.y2026,
      Total: totalPermitValueM_2026,
    },
  ];

  const resPermitTrend = [
    {
      year: "2024",
      Total: RES_TRENDS.totalPermits.y2024,
      "Single-Family": RES_TRENDS.singleFamily.y2024,
      "Multi-Family": RES_TRENDS.multiFamily.y2024,
      Mobile: RES_TRENDS.mobile.y2024,
    },
    {
      year: "2025",
      Total: RES_TRENDS.totalPermits.y2025,
      "Single-Family": RES_TRENDS.singleFamily.y2025,
      "Multi-Family": RES_TRENDS.multiFamily.y2025,
      Mobile: RES_TRENDS.mobile.y2025,
    },
    {
      year: "2026",
      Total: RES_TRENDS.totalPermits.y2026,
      "Single-Family": RES_TRENDS.singleFamily.y2026,
      "Multi-Family": RES_TRENDS.multiFamily.y2026,
      Mobile: RES_TRENDS.mobile.y2026,
    },
  ];

  const nonresByCategory = nonres2025.map((r) => ({ name: r.name, value: r.value }));

  // ---------- KPI headline deltas ----------
  const resValueYoY_2025 = yoyPct(PERMIT_OVERVIEW.residentialValueM.y2025, PERMIT_OVERVIEW.residentialValueM.y2024);
  const resValueYoY_2026 = yoyPct(PERMIT_OVERVIEW.residentialValueM.y2026, PERMIT_OVERVIEW.residentialValueM.y2025);

  const nonresValueYoY_2025 = yoyPct(PERMIT_OVERVIEW.nonresValueM.y2025, PERMIT_OVERVIEW.nonresValueM.y2024);
  const nonresValueYoY_2026 = yoyPct(PERMIT_OVERVIEW.nonresValueM.y2026, PERMIT_OVERVIEW.nonresValueM.y2025);

  const totalPermitsYoY_2025 = yoyPct(RES_TRENDS.totalPermits.y2025, RES_TRENDS.totalPermits.y2024);
  const totalPermitsYoY_2026 = yoyPct(RES_TRENDS.totalPermits.y2026, RES_TRENDS.totalPermits.y2025);

  const homeownDelta_24_26 = delta(RES_TRENDS.homeownershipPct.y2026, RES_TRENDS.homeownershipPct.y2024);

  return (
    <div>
      {/* KPI STRIP */}
      <Row className="g-3">
        <Col md={3}>
          <MiniKpi
            label="Residential Permit Value (2026f)"
            value={fmt.usdM0(PERMIT_OVERVIEW.residentialValueM.y2026)}
            hint={`2025e→2026f: ${fmt.pct1(resValueYoY_2026)} • 2024→2025e: ${fmt.pct1(resValueYoY_2025)}`}
            tone="red"
          />
        </Col>
        <Col md={3}>
          <MiniKpi
            label="Nonres Permit Value (2026f)"
            value={fmt.usdM0(PERMIT_OVERVIEW.nonresValueM.y2026)}
            hint={`2025e→2026f: ${fmt.pct1(nonresValueYoY_2026)} • 2024→2025e: ${fmt.pct1(nonresValueYoY_2025)}`}
            tone="dark"
          />
        </Col>
        <Col md={3}>
          <MiniKpi
            label="Total Residential Permits (2025e)"
            value={fmt.int(RES_TRENDS.totalPermits.y2025)}
            hint={`YoY 2024→2025e: ${fmt.pct1(totalPermitsYoY_2025)} • 2025e→2026f: ${fmt.pct1(totalPermitsYoY_2026)}`}
          />
        </Col>
        <Col md={3}>
          <MiniKpi
            label="Homeownership Rate (2026f)"
            value={fmt.pct1(RES_TRENDS.homeownershipPct.y2026)}
            hint={`2024→2026f change: ${homeownDelta_24_26.toFixed(2)} pts`}
            tone="red"
          />
        </Col>
      </Row>

      {/* INSIGHT CARDS */}
      <Row className="g-3 mt-2">
        <Col lg={6}>
          <CardShell style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.06), #fff)" }}>
            <div style={{ fontWeight: 1000, color: BRAND.ink, fontSize: 16 }}>2025 Permit Value Mix</div>
            <div style={{ color: BRAND.slate, fontSize: 13.5, marginTop: 6, lineHeight: 1.6 }}>
              In 2025e, total permit value is <b style={{ color: BRAND.ink }}>{fmt.usdM0(totalPermitValueM_2025)}</b>.
              Residential accounts for <b style={{ color: BRAND.ink }}>{resShare2025.toFixed(1)}%</b> and nonresidential{" "}
              <b style={{ color: BRAND.ink }}>{nonresShare2025.toFixed(1)}%</b>.
            </div>
          </CardShell>
        </Col>

        <Col lg={6}>
          <CardShell style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.06), #fff)" }}>
            <div style={{ fontWeight: 1000, color: BRAND.ink, fontSize: 16 }}>2025 Residential Permit Mix</div>
            <div style={{ color: BRAND.slate, fontSize: 13.5, marginTop: 6, lineHeight: 1.6 }}>
              2025e total permits: <b style={{ color: BRAND.ink }}>{fmt.int(RES_TRENDS.totalPermits.y2025)}</b>.
              Mix: <b style={{ color: BRAND.ink }}>{mix2025.single.toFixed(1)}%</b> single-family,{" "}
              <b style={{ color: BRAND.ink }}>{mix2025.multi.toFixed(1)}%</b> multi-family,{" "}
              <b style={{ color: BRAND.ink }}>{mix2025.mobile.toFixed(1)}%</b> mobile/cabins.
            </div>
          </CardShell>
        </Col>
      </Row>

      {/* CHARTS */}
      <Row className="g-3 mt-2">
        <Col lg={7}>
          <CardShell>
            <div style={{ fontWeight: 1000, color: BRAND.ink, marginBottom: 8 }}>
              Permit Value Trend (Millions) — Residential vs Nonresidential
            </div>
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={permitValueTrend} margin={{ left: 10, right: 10, top: 10, bottom: 10 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="year" {...axisProps} />
                  <YAxis tickFormatter={(v) => `$${v}M`} {...axisProps} />
                  <Tooltip content={<SoftTooltip formatValue={(v) => fmt.usdM0(v)} />} />
                  <Legend wrapperStyle={{ color: BRAND.slate, fontWeight: 900 }} />
                  <Line type="monotone" dataKey="Residential" stroke={CHART.green} strokeWidth={3} dot />
                  <Line type="monotone" dataKey="Nonresidential" stroke={CHART.orange} strokeWidth={3} dot />
                  <Line type="monotone" dataKey="Total" stroke={CHART.purple} strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardShell>
        </Col>

        <Col lg={5}>
          <CardShell>
            <div style={{ fontWeight: 1000, color: BRAND.ink, marginBottom: 8 }}>
              Residential Permits — Total & Composition (2024–2026)
            </div>
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={resPermitTrend} margin={{ left: 10, right: 10, top: 10, bottom: 10 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="year" {...axisProps} />
                  <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} {...axisProps} />
                  <Tooltip content={<SoftTooltip formatValue={(v) => fmt.int(v)} />} />
                  <Legend wrapperStyle={{ color: BRAND.slate, fontWeight: 900 }} />
                  <Bar dataKey="Single-Family" fill={CHART.blue} radius={[10, 10, 0, 0]} stackId="a" />
                  <Bar dataKey="Multi-Family" fill={CHART.rose} stackId="a" />
                  <Bar dataKey="Mobile" fill={CHART.orange} stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardShell>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col lg={12}>
          <CardShell>
            <div className="d-flex align-items-end justify-content-between flex-wrap gap-2">
              <div>
                <div style={{ fontWeight: 1000, color: BRAND.ink, marginBottom: 6 }}>
                  Nonresidential Value by Category (2025e) — Millions
                </div>
                <div style={{ color: BRAND.slate, fontSize: 13.5 }}>
                  Top category: <b style={{ color: BRAND.ink }}>{topNonres2025?.name}</b>{" "}
                  (<b style={{ color: BRAND.ink }}>{fmt.usdM0(topNonres2025?.value)}</b>,{" "}
                  <b style={{ color: BRAND.ink }}>{topNonres2025?.share.toFixed(1)}%</b> of shown categories)
                </div>
              </div>

              <div
                style={{
                  padding: "6px 10px",
                  borderRadius: 999,
                  background: "rgba(204,0,0,0.08)",
                  border: "1px solid rgba(204,0,0,0.18)",
                  color: BRAND.red,
                  fontWeight: 900,
                  fontSize: 12,
                }}
              >
                2025e Nonres Total (shown): {fmt.usdM0(nonres2025Sum)}
              </div>
            </div>

            <div style={{ height: 340, marginTop: 10 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={nonresByCategory} margin={{ left: 10, right: 10, top: 10, bottom: 10 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="name" interval={0} tick={{ ...axisProps.tick, fontSize: 11 }} {...axisProps} />
                  <YAxis tickFormatter={(v) => `$${v}M`} {...axisProps} />
                  <Tooltip content={<SoftTooltip formatValue={(v) => fmt.usdM0(v)} />} />
                  <Legend wrapperStyle={{ color: BRAND.slate, fontWeight: 900 }} />
                  <Bar dataKey="value" name="Value (Millions)" fill={CHART.green} radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardShell>
        </Col>
      </Row>

      <div style={{ marginTop: 14, color: BRAND.slate, fontSize: 12.5 }}>
        Section D shown as KPI summary only (no tables). All calculations are derived only from your Section D values (2024 actual, 2025 estimate, 2026 forecast).
      </div>
    </div>
  );
}

export default function KCGSandbox3() {
  // Demographics slider
  const [year, setYear] = useState(2050);

  // UI toggle
  const [compact, setCompact] = useState(false);

  // Accordion: default all closed
  const [openKey, setOpenKey] = useState(null);

  // DEMOGRAPHICS computed
  const demo = useMemo(() => demoAtYear(year), [year]);

  const stateRow = useMemo(() => POP_BY_COUNTY.find((r) => r.county === "State of Utah"), []);
  const statewidePop = useMemo(() => countyPopAtYear(stateRow, year), [stateRow, year]);

  const countyChartData = useMemo(() => {
    return POP_BY_COUNTY.filter((r) => r.county !== "State of Utah")
      .map((r) => ({ county: r.county, pop: countyPopAtYear(r, year) }))
      .sort((a, b) => b.pop - a.pop)
      .slice(0, 10);
  }, [year]);

  // FINTECH / LIFE SCI
  const finAssetBars = useMemo(
    () => [
      { name: "UT Offices Deposits", valueB: FIN_SERV.depositsUtOfficesB },
      { name: "UT HQ Banks Assets", valueB: FIN_SERV.utBanksAssetsB },
      { name: "Credit Unions Assets", valueB: FIN_SERV.creditUnionAssetsB },
    ],
    []
  );

  const lifeBars = useMemo(
    () => [
      { name: "Employee Jobs", value: LIFE_SCI.jobs },
      { name: "Establishments", value: LIFE_SCI.establishments },
    ],
    []
  );

  // SENTIMENT
  const sentimentLine = useMemo(() => SENTIMENT_POINTS, []);
  const sentimentAvgBars = useMemo(() => SENTIMENT_AVG, []);

  // CONSTRUCTION / HOUSING
  const construction = useMemo(() => CONSTRUCTION, []);
  const latestEst = useMemo(() => CONSTRUCTION.find((r) => r.year === "2025e"), []);
  const nextFc = useMemo(() => CONSTRUCTION.find((r) => r.year === "2026f"), []);

  const accItemStyle = {
    borderRadius: 18,

    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
    background: BRAND.card,
    marginBottom: 12,
  };

  return (
    <>
      <LandingSection
        data={{
          imageName: "KEMC.webp",
          title: "KCG Gardner",
          breadcrumbs: ["Home", "Gardner Gardner"],
          visible: "",
        }}
      />

      {/* HERO */}
      <div className="container mx-auto px-4 sm:px-0" style={{ marginTop: 30, marginBottom: 18 }}>
        <div
          style={{
            borderRadius: 22,
            padding: compact ? 18 : 26,
            background: `radial-gradient(1200px 420px at 20% -10%, rgba(204,0,0,0.18), transparent 55%),
                         radial-gradient(900px 380px at 110% 20%, rgba(15,23,42,0.10), transparent 60%),
                         linear-gradient(135deg, rgba(255,255,255,0.95), rgba(245,247,251,0.95))`,
            border: "1px solid rgba(15,23,42,0.08)",
            boxShadow: "0 16px 50px rgba(2,6,23,0.10)",
          }}
        >
          <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
            <div style={{ minWidth: 280 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 12px",
                  borderRadius: 999,
                  background: "rgba(204,0,0,0.10)",
                  border: "1px solid rgba(204,0,0,0.20)",
                  color: BRAND.red,
                  fontWeight: 900,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}
              >
                Interactive Executive Summary
              </div>

              <h1
                style={{
                  marginTop: 12,
                  marginBottom: 10,
                  fontSize: compact ? 34 : 44,
                  fontWeight: 1100,
                  color: BRAND.ink,
                  letterSpacing: -0.8,
                  lineHeight: 1.05,
                }}
              >
                KCG Gardner<span style={{ color: BRAND.red }}>.</span>
              </h1>

              <p
                style={{
                  margin: 0,
                  maxWidth: 900,
                  color: BRAND.slate,
                  fontSize: 16.5,
                  lineHeight: 1.7,
                }}
              >
                Research-grade KPIs turned into a clean, drillable web experience — built directly from the
               <a href="https://d36oiwf74r1rap.cloudfront.net/wp-content/uploads/2026/01/ERG2026.pdf"> <span style={{ color: BRAND.ink, fontWeight: 900 }}> 2026 Economic Report to the Governor</span>.</a>
              </p>
            </div>

            <div className="d-flex align-items-center gap-2">
             

              
            </div>
          </div>
        </div>
      </div>

      <Container className="mb-5" style={{ marginTop: 18 }}>
        <Accordion activeKey={openKey} onSelect={(k) => setOpenKey(k)} alwaysOpen={false}>
          {/* ======================= SECTION A ======================= */}
          <Accordion.Item eventKey="A" style={accItemStyle}>
            <Accordion.Header>
              <SectionTitle
                icon={Users}
                title="Section A — The “New Utah” Demographic Shift"
                subtitle="County population projections + age-structure trends (interpolated for in-between years like 2050)."
              />
            </Accordion.Header>

            <Accordion.Body style={{ background: BRAND.mist }}>
              <CardShell style={{ background: "linear-gradient(135deg, rgba(255,255,255,1), rgba(245,247,251,1))" }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <NiceSlider value={year} min={2025} max={2065} onChange={setYear} />
                  <div style={{ fontSize: 12.5, color: BRAND.slate }}>
                    
                  </div>
                </div>

                <Row className="g-3 mt-1">
                  <Col md={3}>
                    <MiniKpi
                      label="Statewide Population"
                      value={fmt.int(statewidePop)}
                      hint="Projected population (interpolated view)."
                      tone="red"
                    />
                  </Col>
                  <Col md={3}>
                    <MiniKpi label="Median Age" value={fmt.num1(demo.medianAge)} hint="Projected median age." />
                  </Col>
                  <Col md={3}>
                    <MiniKpi
                      label="Working Age (18–64)"
                      value={`${fmt.int(demo.working)} (${pct(demo.workingShare, 1)})`}
                      hint="Total + share of population."
                      tone="dark"
                    />
                  </Col>
                  <Col md={3}>
                    <MiniKpi
                      label="Retirement Age (65+)"
                      value={`${fmt.int(demo.retire)} (${pct(demo.retireShare, 1)})`}
                      hint="Aging pressure indicator."
                    />
                  </Col>
                </Row>

                <Row className="g-3 mt-2">
                  <Col md={7}>
                    <CardShell style={{ padding: compact ? 12 : 16 }}>
                      <div style={{ fontWeight: 1000, color: BRAND.ink, marginBottom: 8 }}>
                        Top Counties by Projected Population ({year})
                      </div>
                      <div style={{ height: 320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={countyChartData} layout="vertical" margin={{ left: 20, right: 10, top: 10 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis type="number" tickFormatter={(v) => `${Math.round(v / 1000)}k`} {...axisProps} />
                            <YAxis type="category" dataKey="county" width={110} {...axisProps} />
                            <Tooltip content={<SoftTooltip formatterOverride={(v) => fmt.int(v)} />} />
                            <Legend wrapperStyle={{ color: BRAND.slate }} />
                            <Bar
                              dataKey="pop"
                              name="Population"
                              radius={[0, 10, 10, 0]}
                              fill={CHART.a}
                              stroke="rgba(0,0,0,0)"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardShell>
                  </Col>

                  <Col md={5}>
                    <CardShell style={{ padding: compact ? 12 : 16 }}>
                      <div style={{ fontWeight: 1000, color: BRAND.ink, marginBottom: 8 }}>
                        Age Structure Trend (Anchors)
                      </div>
                      <div style={{ height: 320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={AGE_GROUPS.map((r) => ({
                              year: r.year,
                              schoolShare: r.schoolShare,
                              workingShare: r.workingShare,
                              retireShare: r.retireShare,
                            }))}
                            margin={{ left: 4, right: 10, top: 10, bottom: 5 }}
                          >
                            <CartesianGrid {...gridProps} />
                            <XAxis dataKey="year" {...axisProps} />
                            <YAxis tickFormatter={(v) => `${v}%`} {...axisProps} />
                            <Tooltip content={<SoftTooltip formatterOverride={(v) => `${v}%`} />} />
                            <Legend wrapperStyle={{ color: BRAND.slate }} />
                            <Line type="monotone" dataKey="schoolShare" name="School Age (5–17)" stroke={CHART.b} strokeWidth={3} dot />
                            <Line type="monotone" dataKey="workingShare" name="Working Age (18–64)" stroke={CHART.c} strokeWidth={3} dot />
                            <Line type="monotone" dataKey="retireShare" name="Retirement (65+)" stroke={CHART.e} strokeWidth={3} dot />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardShell>
                  </Col>
                </Row>
              </CardShell>
            </Accordion.Body>
          </Accordion.Item>

          {/* ======================= SECTION B ======================= */}
       <Accordion.Item eventKey="B" style={accItemStyle}>
  <Accordion.Header>
    <SectionTitle
      icon={Building2}
      title="Section B — Economic Impact of Fintech / Life Sciences"
      subtitle="KPI summary view."
    />
  </Accordion.Header>

  <Accordion.Body style={{ background: BRAND.mist }}>
    <SectionBImpact BRAND={BRAND} />
  </Accordion.Body>
</Accordion.Item>

          {/* ======================= SECTION C ======================= */}
          <Accordion.Item eventKey="C" style={accItemStyle}>
            <Accordion.Header>
              <SectionTitle
                icon={MapPinned}
                title="Section C — Utah Consumer Sentiment Index"
                subtitle="Key points from the 2025 sentiment path and multi-year context."
              />
            </Accordion.Header>

            <Accordion.Body style={{ background: BRAND.mist }}>
              <CardShell>
                <Row className="g-3">
                  <Col md={3}>
                    <MiniKpi label="Utah Avg (2025)" value="81.0" hint="Annual average sentiment index." tone="red" />
                  </Col>
                  <Col md={3}>
                    <MiniKpi label="U.S. Avg (2025)" value="57.6" hint="Annual average national sentiment index." />
                  </Col>
                  <Col md={3}>
                    <MiniKpi label="Year-Low (Oct 2025)" value="73.4" hint="Lowest point in 2025." tone="dark" />
                  </Col>
                  <Col md={3}>
                    <MiniKpi label="End of Year (Dec 2025)" value="79.9" hint="Ended year down vs January." />
                  </Col>
                </Row>

                <Row className="g-3 mt-2">
                  <Col md={7}>
                    <CardShell>
                      <div style={{ fontWeight: 1000, color: BRAND.ink, marginBottom: 8 }}>
                        2025 Path (Selected Monthly Points)
                      </div>
                      <div style={{ height: 320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={sentimentLine} margin={{ left: 6, right: 10, top: 10 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis dataKey="label" {...axisProps} />
                            <YAxis {...axisProps} />
                            <Tooltip content={<SoftTooltip />} />
                            <Legend wrapperStyle={{ color: BRAND.slate }} />
                            <Line type="monotone" dataKey="utah" name="Utah Index" stroke={CHART.a} strokeWidth={3} dot />
                            <Line type="monotone" dataKey="usAvg" name="U.S. Avg (2025)" stroke={CHART.b} strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={{ color: BRAND.slate, fontSize: 12.5, marginTop: 8, lineHeight: 1.4 }}>
                        Note: April point uses the report’s stated “declined by 11.4 points” from January.
                      </div>
                    </CardShell>
                  </Col>

                  <Col md={5}>
                    <CardShell>
                      <div style={{ fontWeight: 1000, color: BRAND.ink, marginBottom: 8 }}>Annual Context</div>
                      <div style={{ height: 320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={sentimentAvgBars} margin={{ left: 6, right: 10, top: 10 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis dataKey="year" {...axisProps} />
                            <YAxis {...axisProps} />
                            <Tooltip content={<SoftTooltip />} />
                            <Legend wrapperStyle={{ color: BRAND.slate }} />
                            <Bar dataKey="utahAvg" name="Utah Avg" radius={[10, 10, 0, 0]} fill={CHART.c} stroke="rgba(0,0,0,0)" />
                            <Bar dataKey="usAvg" name="U.S. Avg (2025)" radius={[10, 10, 0, 0]} fill={CHART.d} stroke="rgba(0,0,0,0)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardShell>
                  </Col>
                </Row>
              </CardShell>
            </Accordion.Body>
          </Accordion.Item>

          {/* ======================= SECTION D ======================= */}
          <Accordion.Item eventKey="D" style={accItemStyle}>
            <Accordion.Header>
              <SectionTitle
                icon={Building2}
                title="Section D — Construction & Housing Inventory Projections"
                subtitle="Construction activity (2020–2026f) + affordability / rent signals."
              />
            </Accordion.Header>
<Accordion.Body style={{ background: BRAND.mist }}>
    <SectionDConstruction BRAND={BRAND} />
  </Accordion.Body>
           
          </Accordion.Item>


        </Accordion>

        {/* CTA */}
        
      </Container>

      <Footer />
    </>
  );
}
